import { UUID } from '@cmg/common';
import { Button, SnackbarManager } from '@cmg/design-system';
import React, { useCallback, useMemo } from 'react';

import { useMatchingCmgEntityKeys } from '../../../../../../../common/hooks/useMatchingCmgEntityKeys';
import { useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess } from '../../../../../../../common/util/check-access/useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess';
import { AllocationAcknowledgement } from '../../../../../../../graphql';
import { updateDemandGridRowsReactiveVar } from '../../../../../../order-book/institutional-demand/hooks/useUpdateDemandGridRowsReactiveVar';
import { canAcknowledgeAllocation } from '../../../../../../order-book/utils';
import { useOfferingSidePanel_UnackknowledgeCoveredInstutionalFinalAllocationMutation } from '../../../../../graphql';
import { useIndicationActivitySalesAndTradingContext } from '../../../context/IndicationActivitySalesAndTradingContext';
import { useIndicationActivity_AcknowledgeCoveredIndicationMutation } from '../graphql/__generated__';

export type Props = Readonly<{
  indicationId?: string;
  allocationVersion?: string;
  offeringId?: UUID;
  investorReplyStatus: AllocationAcknowledgement;
  billingAndDeliveryAgentCmgEntityKey: string | null;
}>;
const useSalesAndTradingAllocation = ({
  indicationId,
  allocationVersion,
  offeringId,
  investorReplyStatus,
  billingAndDeliveryAgentCmgEntityKey,
}: Props) => {
  const {
    access: { canBillingAndDeliveryAgentUpdateAllocationAcknowledgment },
  } = useIndicationActivitySalesAndTradingContext();

  const matchingCmgEntityKeys = useMatchingCmgEntityKeys();

  const { canUpdate: canUpdateInstitutionalIndication } =
    useGetPublishedOfferingEntitlementsAndCheckInstitutionalIndicationAccess({
      offeringId,
    });

  const canAcknowledge = canAcknowledgeAllocation({
    canBndAgentUpdateInstitutionalIndication:
      canBillingAndDeliveryAgentUpdateAllocationAcknowledgment,
    canUserUpdateInstitutionalIndication: canUpdateInstitutionalIndication,
    matchingCmgEntityKeys,
    bndAgentCmgEntityKey: billingAndDeliveryAgentCmgEntityKey,
  });

  const [
    acknowledgeCoveredAllocation,
    { loading: acknowledgeCoveredAllocationLoading, error: acknowledgeCoveredAllocationError },
  ] = useIndicationActivity_AcknowledgeCoveredIndicationMutation();

  const [
    unacknowledgeCoveredAllocation,
    { loading: unacknowledgeCoveredAllocationLoading, error: unacknowledgeCoveredAllocationError },
  ] = useOfferingSidePanel_UnackknowledgeCoveredInstutionalFinalAllocationMutation();

  const errors = useMemo(
    () =>
      [acknowledgeCoveredAllocationError, unacknowledgeCoveredAllocationError]
        .map(error => error?.message)
        .filter((val): val is string => Boolean(val)),
    [acknowledgeCoveredAllocationError, unacknowledgeCoveredAllocationError]
  );

  const handleAcknowledge = useCallback(async () => {
    try {
      if (!offeringId || !indicationId || !allocationVersion) {
        throw new Error('Missing offeringId, indicationId or allocationVersion');
      }

      await acknowledgeCoveredAllocation({
        variables: {
          offeringId,
          indicationId,
          versionId: allocationVersion,
        },
      });

      SnackbarManager.success('Successfully acknowledged allocation');

      updateDemandGridRowsReactiveVar({ fetchIds: [indicationId] });
    } catch {
      SnackbarManager.error('Failed to acknowledge allocation');
    }
  }, [acknowledgeCoveredAllocation, allocationVersion, indicationId, offeringId]);

  const handleUnacknowledge = useCallback(async () => {
    try {
      if (!offeringId || !indicationId || !allocationVersion) {
        throw new Error('Missing offeringId, indicationId or allocationVersion');
      }

      await unacknowledgeCoveredAllocation({
        variables: {
          offeringId,
          indicationId,
          versionId: allocationVersion,
        },
      });

      SnackbarManager.success('Successfully unacknowledged allocation');

      updateDemandGridRowsReactiveVar({ fetchIds: [indicationId] });
    } catch {
      SnackbarManager.error('Failed to unacknowledge allocation');
    }
  }, [allocationVersion, indicationId, offeringId, unacknowledgeCoveredAllocation]);

  const isAcknowledgementVisible =
    canAcknowledge && investorReplyStatus === AllocationAcknowledgement.Unacknowledged;
  const isUndoVisible =
    canAcknowledge &&
    (investorReplyStatus === AllocationAcknowledgement.Rejected ||
      investorReplyStatus === AllocationAcknowledgement.Acknowledged);

  const actionButtons = useMemo(
    () => [
      ...(isAcknowledgementVisible
        ? [
            <Button
              key="acknowledge"
              variant="contained"
              onClick={() => handleAcknowledge()}
              disabled={acknowledgeCoveredAllocationLoading}
            >
              Acknowledge
            </Button>,
          ]
        : []),
      ...(isUndoVisible
        ? [
            <Button
              key="undo"
              variant="outlined"
              onClick={handleUnacknowledge}
              disabled={unacknowledgeCoveredAllocationLoading}
            >
              Undo
            </Button>,
          ]
        : []),
    ],
    [
      acknowledgeCoveredAllocationLoading,
      handleAcknowledge,
      handleUnacknowledge,
      isAcknowledgementVisible,
      isUndoVisible,
      unacknowledgeCoveredAllocationLoading,
    ]
  );

  return {
    actionButtons,
    errors,
  };
};

export default useSalesAndTradingAllocation;
